import React, { useState } from "react";
import HorizontalRule from "../../../components/components/atoms/HorizontalRule";
import { Badge, Button, Container, Heading, Icon } from "@telia/teddy";
import { useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import PAGES from "../../../../../constants/pages";
import { RootState } from "../../../../../models";
import { getNumberOfItemsInCart } from "../../../checkout/state/selectors/cartSelectors";
import ConnectedShoppingCart from "../../../navigation/components/organisms/ConnectedShoppingCart/ConnectedShoppingCart";
import ContactCard from "../../components/molecules/ContactCard/ContactCard";
import ContinueOrderModal from "../../components/molecules/ContinueOrderModal/ContinueOrderModal";
import DeleteDraftOrderModal from "../../components/molecules/DeleteDraftOrderModal/DeleteDraftOrderModal";
import Status from "../../components/molecules/Status/Status";
import {
    getCustomerCreditCheck,
    getCustomerExistingAccounts,
    getCustomerLegalOwnerIndividual,
} from "../../state/selectors/customerSelectors";
import { requiresCreditCheck as requiresCreditCheckSelector } from "../../state/selectors/creditCheckSelectors";
import { getFormById } from "../../state/selectors/formSelectors";
import { getSalesAgent } from "../../state/selectors/selectors";
import { API_URL_DELETE_DRAFT_ORDER } from "../../api";
import { retailerFetch } from "../../retailerFetch";
import { goBackToOrdersOverview } from "../../utils";
import "./DraftOrderDetail.less";

const DraftOrderDetail = () => {
    const { orderRef = "" } = useParams();
    const [showContinueModal, setShowContinueModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const deleteMutation = useMutation({
        mutationFn: () =>
            retailerFetch(API_URL_DELETE_DRAFT_ORDER(orderRef), { method: "DELETE" }).then((res) => {
                if (!res.ok) throw new Error("Kunne ikke slette ordreutkastet. Prøv igjen senere.");
            }),
        onSuccess: () => {
            window.location.assign(PAGES.RETAILER.ORDERS_OVERVIEW.URL);
        },
    });

    const legalOwner = useSelector((state: RootState) => getCustomerLegalOwnerIndividual(state));
    const creditCheck = useSelector((state: RootState) => getCustomerCreditCheck(state));
    const requiresCreditCheck = useSelector((state: RootState) => requiresCreditCheckSelector(state));
    const existingAccounts = useSelector((state: RootState) => getCustomerExistingAccounts(state));
    const isExistingCustomer = existingAccounts.length > 0;
    const salesAgent = useSelector((state: RootState) => getSalesAgent(state));
    const numberOfCartItems = useSelector((state: RootState) => getNumberOfItemsInCart(state));
    const identificationForm = useSelector((state: RootState) =>
        getFormById(state, "customerIdentificationForm" as any),
    );

    const legalOwnerFullName = `${legalOwner?.firstName || ""} ${legalOwner?.lastName || ""}`.trim();
    const legalOwnerAddress = legalOwner?.street || "";
    const legalOwnerCity = legalOwner?.city || "";
    const legalOwnerEmail = legalOwner?.email || "";
    const legalOwnerPhone = legalOwner?.phone || "";

    const salesAgentFullName = salesAgent
        ? `${salesAgent?.firstName || ""} ${salesAgent?.lastName?.charAt(0) || ""}`.trim()
        : "";

    const identificationMethod = identificationForm?.values?.selectedIdentificationMethod;
    const hasIdentification = Boolean(identificationMethod);

    return (
        <div className="orders">
            <Container>
                <Button variant="secondary" size="sm" onClick={goBackToOrdersOverview} className="orders__back-button">
                    <Icon name="arrow-left" size="sm" aria-hidden="true" focusable="false" />
                    Tilbake
                </Button>
                <div className="draft-order-detail__layout">
                    <div className="draft-order-detail__main">
                        <div className="draft-order-detail__ref">
                            <Heading as="h2" variant="title-100" mb="50">
                                Ordrereferanse
                            </Heading>
                            <Badge variant="neutral" hideIcon>
                                {orderRef}
                            </Badge>
                        </div>
                        <HorizontalRule />
                        <div className="retailer-summary-contact-cards">
                            {legalOwnerFullName && (
                                <div className="retailer-summary-contact-card-section">
                                    <Heading as="h2" variant="title-100" mb="50">
                                        Kunde
                                    </Heading>
                                    <ContactCard
                                        name={legalOwnerFullName}
                                        address={legalOwnerAddress}
                                        city={legalOwnerCity}
                                        contactPhone={legalOwnerPhone}
                                        contactEmail={legalOwnerEmail}
                                    />
                                </div>
                            )}
                            {salesAgent && (
                                <div className="retailer-summary-contact-card-section">
                                    <Heading as="h2" variant="title-100" mb="50">
                                        Salgsagent
                                    </Heading>
                                    <ContactCard name={salesAgentFullName} address={salesAgent?.dealerName ?? ""} />
                                </div>
                            )}
                        </div>
                        <HorizontalRule />
                        <div className="retailer-summary-status">
                            <Heading as="h2" variant="title-100" mb="100">
                                Status
                            </Heading>
                            <Status
                                hasCreditCheck={creditCheck?.performed || false}
                                hasIdentification={hasIdentification}
                                hasLegalOwner={!!legalOwnerFullName}
                                identificationMethod={identificationMethod}
                                requiresCreditCheck={requiresCreditCheck}
                                creditCheckDecision={creditCheck?.decision}
                                creditCheckMessage={creditCheck?.message}
                                isExistingCustomer={isExistingCustomer}
                            />
                        </div>
                    </div>
                    {numberOfCartItems > 0 && (
                        <div className="draft-order-detail__cart">
                            <ConnectedShoppingCart useStandalone isRetailer isReadonly />
                        </div>
                    )}
                    <div className="draft-order-detail__actions">
                        <Button variant="primary" onClick={() => setShowContinueModal(true)}>
                            Fortsett ordren
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                deleteMutation.reset();
                                setShowDeleteModal(true);
                            }}
                        >
                            Slett ordreutkast
                        </Button>
                    </div>
                </div>
            </Container>
            {showContinueModal && (
                <ContinueOrderModal orderRef={orderRef} onClose={() => setShowContinueModal(false)} />
            )}
            {showDeleteModal && (
                <DeleteDraftOrderModal
                    isDeleting={deleteMutation.isPending}
                    error={deleteMutation.error?.message}
                    onClose={() => {
                        deleteMutation.reset();
                        setShowDeleteModal(false);
                    }}
                    onConfirm={() => deleteMutation.mutate()}
                />
            )}
        </div>
    );
};

export default DraftOrderDetail;
